import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  deleteLocalStorageValue,
  getLocalStorageValue,
  prefixStorageKey,
  setLocalStorageValue,
} from '@antimatterhq/ui/utils/localStorage';

interface InstanceIdContextType {
  instanceId: string | null;
  clearInstanceId: () => void;
  storeInstanceId: (instanceId: string, shouldSetInstanceId?: boolean) => void;
}

const InstanceIdContext = createContext<InstanceIdContextType>({
  instanceId: null,
  clearInstanceId: () => {},
  storeInstanceId: () => {},
});

function getInstanceIDFromLocalStorage() {
  return getLocalStorageValue<string>(prefixStorageKey('instanceID')) ?? null;
}

function clearStoredInstanceIDs(): void {
  deleteLocalStorageValue(prefixStorageKey('instanceID'));
}

const getInstanceIDFromPath = (): string | null => {
  const pathParts = window.location.pathname.split('/');
  const maybeInstanceID = pathParts[1];
  return !maybeInstanceID ? null : maybeInstanceID;
};

function getInstanceID() {
  const instanceID = getInstanceIDFromPath() ?? getInstanceIDFromLocalStorage();
  return instanceID;
}

export const InstanceIdProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [instanceId, setInstanceId] = useState<string | null>(getInstanceID());

  useEffect(() => {
    if (instanceId && !window.location.pathname.includes(instanceId!)) {
      window.history.replaceState({}, '', `/${instanceId}/home`);
    }
    if (instanceId && window.location.pathname.includes(instanceId!)) {
      const pathParts = window.location.pathname.split(instanceId!);
      // If there is nothing after the instanceId or the path is blank after the instanceId, redirect to home
      if (pathParts.length === 1 || !pathParts[1]) {
        window.history.replaceState({}, '', `/${instanceId}/home`);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const localStorageInstanceID = getInstanceIDFromLocalStorage();

    if (localStorageInstanceID !== instanceId) {
      setLocalStorageValue(prefixStorageKey('instanceID'), instanceId);
    }
  }, [instanceId]);

  const clearInstanceId = useCallback(() => {
    clearStoredInstanceIDs();
    setInstanceId('');
  }, []);

  const storeInstanceId = useCallback(
    (iid: string, shouldSetInstanceId: boolean = true) => {
      setLocalStorageValue(prefixStorageKey('instanceID'), iid);

      // This is used on login to prevent screen from flashing the sidebar
      if (shouldSetInstanceId) {
        setInstanceId(iid);
      }
    },
    [],
  );

  const value = useMemo(
    () => ({
      instanceId,
      clearInstanceId,
      storeInstanceId,
    }),
    [instanceId, clearInstanceId, storeInstanceId],
  );

  return (
    <InstanceIdContext.Provider value={value}>
      {children}
    </InstanceIdContext.Provider>
  );
};

export const useInstanceId = () => {
  const context = useContext(InstanceIdContext);

  if (context == null) {
    throw new Error('InstanceIdContext not found');
  }

  return context;
};
